import React, { useState, useEffect } from 'react';
import {
  Popover,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from 'date-fns';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const DatePickerPopover = ({
  anchorEl,
  open,
  onClose,
  selectedDate,
  onSelectDate,
}) => {
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  // Keep viewDate in sync when popover opens or selectedDate changes
  useEffect(() => {
    if (open) {
      setViewDate(selectedDate || new Date());
    }
  }, [open, selectedDate]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const handlePrevMonth = () => {
    setViewDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const handleDateClick = (dayDate) => {
    onSelectDate(dayDate);
    onClose();
  };

  const handleTodayClick = () => {
    const today = new Date();
    onSelectDate(today);
    onClose();
  };

  // Generate days for 7-column calendar starting Monday
  const firstDayOfMonth = new Date(year, month, 1);
  const dayOfWeek = firstDayOfMonth.getDay();
  // Monday as index 0 (Sun = 6, Mon = 0, ..., Sat = 5)
  const startOffset = (dayOfWeek + 6) % 7;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];

  // Trailing days from previous month
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, daysInPrevMonth - i);
    calendarDays.push({
      date: d,
      dayNumber: daysInPrevMonth - i,
      isCurrentMonth: false,
    });
  }

  // Days in current month
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(year, month, i);
    calendarDays.push({
      date: d,
      dayNumber: i,
      isCurrentMonth: true,
    });
  }

  // Leading days from next month to complete rows of 7
  const remaining = (7 - (calendarDays.length % 7)) % 7;
  for (let i = 1; i <= remaining; i++) {
    const d = new Date(year, month + 1, i);
    calendarDays.push({
      date: d,
      dayNumber: i,
      isCurrentMonth: false,
    });
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 2,
          width: '286px',
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)',
          border: '1px solid #E5E7EB',
          mt: 1,
        },
      }}
    >
      {/* Month & Year Header with Navigation Arrows */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={handlePrevMonth}
          sx={{
            p: '4px',
            color: '#374151',
            '&:hover': { bgcolor: '#F3F4F6' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '14.5px',
            color: '#111827',
          }}
        >
          {format(viewDate, 'MMMM yyyy')}
        </Typography>

        <IconButton
          size="small"
          onClick={handleNextMonth}
          sx={{
            p: '4px',
            color: '#374151',
            '&:hover': { bgcolor: '#F3F4F6' },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Weekdays Header (Mo, Tu, We, Th, Fr, Sa, Su) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          mb: 1,
          textAlign: 'center',
        }}
      >
        {WEEKDAYS.map((day) => (
          <Typography
            key={day}
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#6B7280',
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Days Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          rowGap: '4px',
          columnGap: '2px',
          justifyItems: 'center',
        }}
      >
        {calendarDays.map((item, index) => {
          const isSelected = isSameDay(item.date, selectedDate);
          return (
            <Box
              key={index}
              onClick={() => handleDateClick(item.date)}
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                fontSize: '13px',
                fontWeight: isSelected ? 700 : item.isCurrentMonth ? 500 : 400,
                cursor: 'pointer',
                bgcolor: isSelected ? '#000000' : 'transparent',
                color: isSelected
                  ? '#FFFFFF'
                  : item.isCurrentMonth
                  ? '#111827'
                  : '#9CA3AF',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isSelected ? '#000000' : '#F3F4F6',
                },
              }}
            >
              {item.dayNumber}
            </Box>
          );
        })}
      </Box>

      {/* Footer Actions (Clear on left, Today on right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2,
          pt: 1,
          borderTop: '1px solid #F3F4F6',
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            color: '#2563EB',
            minWidth: 'auto',
            p: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Clear
        </Button>

        <Button
          size="small"
          onClick={handleTodayClick}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            color: '#2563EB',
            minWidth: 'auto',
            p: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Today
        </Button>
      </Box>
    </Popover>
  );
};

export default DatePickerPopover;
